<template>
<div class="chapter">
<div class="inner_page">
<h3 class="calibre14" id="PART_I_CHAPTER_V">CHAPTER V.<br class="calibre9"/>
<span class="smaller">THE FUTURE OF THE POLAR QUESTION.</span></h3>
<p class="calibre3">1. The eagerness of human nature for gain and material
prosperity is so great, that we are wont to estimate the value
of all undertakings by the standard of utility; and too often
it is forgotten, that each generation is destined to fulfil the
task of acquiring and collecting the knowledge which is to
benefit only a later generation. If, then, the Polar question
be valueless for our material interests, is it therefore valueless
for science? and assuming that it is for the present worthless
as far as gain and wealth are concerned, must it continue
so for all time? Not that we are entitled, even from this
narrower point of view, to deny the usefulness of Polar
exploration, as Cook seems to have done when he said, “Never
from those regions will any advantage accrue to our race;”
but rather bear in mind what Sir James Ross tells us: “The
profit which accrued to England, in each year after the voyage
(1818) of my uncle (Sir John Ross) in North Baffin’s Bay, from
those rediscovered parts of the Arctic seas, was more than
enough to defray all the expenses of the voyages of discovery
undertaken from 1818 to 1838.” Scoresby with his single ship
made a million thalers by the capture of whales, and the
Americans had for many years a clear profit of eight million
dollars from the fisheries of the frozen seas of Behring’s
Straits. There were also, it is true, very considerable losses;
for, in 1830, nineteen English ships engaged in the whale
fishery were “beset” in the ice of Melville Bay, and nearly
all destroyed; in 1871, twenty-six American ships were
crushed to pieces in Behring’s Straits, and as many as seventy-three<span id="pgepubid00060" title="[33]"><a class="reference" id="Page_33" title="[33]"></a></span>
Dutch vessels sank in one year in the seventeenth
century from the pressure of the ice.</p>
<p class="calibre3">2. We do not, however, mean to assert, that the progress of
Polar discovery is always followed by a corresponding increase
in the capture of fish in the Arctic seas. On the contrary,
the take of oil-yielding animals is steadily decreasing, and
even if an open sea should be discovered in 82° N.L., in
which whales should be found in as great abundance as ice-floes
unhappily are, the whaler with his poor equipment would
never be able to follow them thither. The fur countries,
once as productive as the mines of Peru, are incapable of
further extension; even the treasures of mammoths’ tusks
have become rare, and in order to bring thirty tons of lignite
from the north-east of Greenland, a ship must expend seventy
tons of sound coal in the transit, besides passing the winter
there. That the teas of China, the silks of Japan, the spices
of the Moluccas will never descend to us from the ice-fields
has long been settled. No one at the present day thinks any
longer of the commercial value of the North-West and North-East
passages. Modes of escape from the perils and caprices
of the ice have grown out of the endeavour to discover routes
of commerce, which lay beyond the reach of the cannon of
the Spaniards at the time when they aspired to the monopoly
of the trade of the world. The reward of 25,000 gulden,
offered by the Dutch government for the discovery of a
North-East passage, and that of £20,000 by the English
parliament for the North-West passage, have never been paid,
because never claimed, nor are they, in the least degree, likely
to be claimed.<a class="reference" id="FNanchor_12"></a><span class="fnanchor" href="#/Text/Section0043#Footnote_12" title="Link target outside of Sample">[12]</span></p>
<p class="calibre3">3. Yet, quite independent of material results, Polar exploration
presents no unworthy object for scientific investigation—a
region of the globe 120,000 square miles in extent never
yet entered by man. The Polar question, as <i class="calibre10">a problem of
science</i>, aims at determining the limits of land and water, at
the perfecting of that network of lines with which comparative
science seeks to surround our planet, even to its Poles.
The completion of this labour will serve to discover those
physical laws which regulate climates, the currents of the<span id="pgepubid00061" title="[34]"><a class="reference" id="Page_34" title="[34]"></a></span>
atmosphere and sea, and the analogies of geology with the
earth as we see it.</p>
<p class="calibre3">4. But how is this to be attained? At first it would appear
as if the methods of ice-navigation had been followed by
such success, that their continued application guaranteed still
greater results. The gradual advance by means of ships,
from the Polar Circle to 73°, 75°, 79°, or even to 82° N.L., has
been the result and is the reward of the labours of three
centuries. But to reach higher degrees, from 82° to 90°,
depends on other conditions than mere time. That increased
experience and boldness have removed many of the inconveniences
and dangers attendant on Arctic navigation is
undoubtedly true, but it is also as true, that, upon the whole,
<i class="calibre10">the safely and convenience of ice-navigation have more steadily
increased than its successes</i>. Hudson, Baffin, and especially
Scoresby, and even some whalers of the seventeenth century,
reached latitudes which have scarcely been exceeded since,
and in many cases this progress was due, not to greater boldness
and experience, but rather to chance and the caprices of
the ice, which “to the whaler often permitted glances into its
interior, which were denied to the scientific explorer.”</p>
<p class="calibre3">5. The greater perfection of our means enables us to conduct
Polar expeditions with greater facility. Instead of
dissipating our strength by sending out several ships, even
small fleets, amounting sometimes to fifteen ships (often not
larger than the boats of a modern Polar ship), since the days
of Sir John Ross, we equip one or two ships only, strongly
built for their “special purpose, provided with steam-power,
and with all that is desirable or requisite; and instead of
despatching them for short summer cruises, we provision them,
send them out for several years, and, by appropriate nourishment
and the aid of medical science, protect the crews from
the scourge of scurvy. In those days, when even the wealthy
lived during the winter on salt beef, and English squires were
obliged at the beginning of winter, on account of the scarcity
of food for the cattle, to kill and salt a portion of their herd,
preserved and antiscorbutic victuals were an impossibility to
a Hudson, a James, a Fox, in their winters amid the ice. Those
introduced by Ross—then called “Donkin’s meat”—have
been greatly improved since, and through them the scurvy,<span id="pgepubid00062" title="[35]"><a class="reference" id="Page_35" title="[35]"></a></span>
which used to carry off whole crews of ships, has lost its
former terrors.</p>
<p class="calibre3">6. In this power to extend our expeditions without danger,
and especially in sledge journeys during the autumn and
spring, which are possible only to expeditions prepared to
winter in the ice, are the grounds why we have not halted at
the barriers “of the bulwarks built for eternity;” in the
Rennselaer harbour, in the Lancaster-Barrow route, or at the
Pendulum islands. It is only sledge expeditions, as Middendorf
says, which have been able to effect results of any
magnitude on the inaccessible coasts of the extreme north;
and the great extent to which the Russians had used sledge
expeditions evidently served as an example both to the
English and to Kane.</p>
<p class="calibre3">7. In Polar expeditions, therefore, we have probably reached,
so far as the exploration of the highest latitudes by means of
ships is concerned, the limits of possibility. The extraordinary
success which fell to the lot of Hall’s expedition <i class="calibre10">teaches
us only the possibility of encroaching but a little beyond that
limit</i>, even under the most favourable circumstances.</p>
<p class="calibre3">8. In all cases where the attempt shall be made to reach
the highest latitudes with a ship, I would again recommend
the route through Smith’s Sound, because, in the first place, I
believe that any considerable advance is only to be expected
in coast-water; and in the second place, because the Grant
Coast offers facilities for sledge expeditions on a large scale.
East Greenland in the higher latitudes, 73°-75°, may be
regarded as inaccessible; and the attempt to penetrate northwards
in its coast-water was a delusion of the second German
North-Pole expedition. In the north of Spitzbergen, and in
Behring’s Straits, fifty expeditions and countless whalers have
heard from the ice an imperious <i class="calibre10">ne plus ultra</i>; and the same
prohibition has been uttered to as many expeditions on the
North-East passage. In both these routes the cause of failure
was the disproportion between what could be reached in one
or two summers, and the vast extent of sea blocked by impenetrable
ice. In like manner, the probability of reaching
the Pole itself with our present resources is so small, and the
attempt to do it is so utterly disproportionate to the sacrifices
exacted and the results achieved, that it would be advisable<span id="pgepubid00063" title="[36]"><a class="reference" id="Page_36" title="[36]"></a></span>
to exclude it from Arctic exploration, until, instead of the
impotent vessels of the sea, we can send thither those of
the air.</p>
<p class="calibre3">9. Be this as it may, the present English North-Pole Expedition
will essentially contribute to solve the question, whether
the Pole can be reached by the route through Upper Smith’s
Sound. This, according to the views of almost all Polar
navigators, holds out the greatest chances for further advance
by sea. Should this expedition, equipped in so effective a
manner, and sent out by a nation of such great experience,
not come nearer to the goal, or, if nearer, only through
sledging—which may very probably be the case—the conviction
will then be strengthened, that all efforts to reach the
Pole by navigation in the Frozen Ocean are hopeless, and
witness only to the glorious persistency of human endeavour.</p>
<p class="calibre3">10. But until aërial navigation to the Pole shall be
attempted, it would be advisable to follow the example of
the Swedes, and, in the service of Natural History and Geography,
content ourselves with the exploration of those Arctic
lands of which, up to the present moment, we know only the
coast-line, or which, situated on the outermost verge of our
Polar charts, are still untrodden by man; we mean specially
Gillis’, Grinnell’s, Wrangel’s Land, and above all, the interior
of Greenland. The Polar question, hitherto regarded chiefly
as a geographical problem, would thus, for a considerable
time, be taken up in the interest of Natural Science. Lieutenant
Weyprecht, after dwelling on the predominance of
exploration in Polar expeditions, expresses a wish, that the
great civilized nations would unite in contemporaneous Arctic
expeditions for magnetical, electrical, and meteorological
investigations: “In order to attain decisive scientific results,
a number of expeditions should be sent to different places in
the Arctic regions to make observations, at the same time,
with similar instruments, and in accordance with similar
instructions.” They who think such results too insignificant for
the energies and sacrifices which are expended to achieve
them, and who would rather that such efforts should be transferred
to those still unknown regions of the earth, which may
become the dwelling-places of man, will, of course, give their
veto against the further agitation of the Arctic question.</p>
<p class="calibre3"><span id="pgepubid00064" title="[37]"><a class="reference" id="Page_37" title="[37]"></a></span></p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0011.vue?4c312e7a